/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'

import {
    AreaChart,
    Area,
    XAxis,
    Tooltip,
    CartesianGrid,
    YAxis,
    Legend,
} from 'recharts'
import { trackEvent } from 'utils/googleAnalytics'

import { DashboardItemProps } from '../types'
import {
    ChartContainer,
    LegendItem,
    LegendLabel,
    LegendTitle,
    LegendWrapper,
    TooltipContainer,
} from './LegendYearOverYearOriginationsChart.styled'

function LegendYearOverYearOriginationsChart({
    data,
    dealershipName,
}: Readonly<DashboardItemProps>) {
    const [interaction, setInteraction] = useState(false)
    const [years, setYears] = useState<number[]>([])

    const colors = [
        'var(--legend-dashboard-primary-color)',
        'var(--legend-dashboard-secondary-color)',
        'var(--legend-dashboard-tertiary-color)',
    ]

    useEffect(() => {
        const uniqueYears = data.reduce((acc: any, item: any) => {
            Object.keys(item).forEach((key) => {
                if (typeof item[key] === 'number') {
                    acc.add(Number(key))
                }
            })

            return acc
        }, new Set<number>())

        const uniqueYearsArray: number[] = Array.from(uniqueYears)

        const lastThreeSortedYears = [...uniqueYearsArray]
            .sort((a, b) => b - a)
            .slice(0, 3)

        setYears(lastThreeSortedYears)
    }, [data])

    function trackInteraction() {
        if (interaction) return

        trackEvent(
            'Loyalty Dashboard',
            'Chart Interaction',
            'dealership',
            dealershipName,
        )

        setInteraction(true)
    }

    const renderLegend = (props: any) => {
        const { payload } = props

        return (
            <LegendWrapper>
                <LegendTitle>Originations</LegendTitle>
                <LegendLabel>Year Over Year</LegendLabel>
                {[...payload].reverse().map((item: any) => (
                    <LegendItem key={item.dataKey}>
                        <div
                            style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                margin: '0 4px 0 0',
                                backgroundColor: item.color,
                                borderRadius: '10px',
                            }}
                        />
                        {item.value}
                    </LegendItem>
                ))}
            </LegendWrapper>
        )
    }

    function formatTooltip(item: any) {
        let info: any = {}

        if (item?.payload?.length) {
            info = item.payload[0].payload
        }

        return (
            <TooltipContainer>
                <ul>
                    {years.map((y: any, index) => (
                        <li key={y}>
                            {info[y] && (
                                <p
                                    style={{
                                        color: colors[index],
                                    }}
                                >
                                    {new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        maximumFractionDigits: 0,
                                    }).format(info[y])}
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            </TooltipContainer>
        )
    }

    return (
        <ChartContainer>
            <div data-testid="chart-container" onMouseEnter={trackInteraction}>
                <AreaChart
                    width={850}
                    height={300}
                    data={data}
                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                >
                    <CartesianGrid
                        stroke="#f0f0f0"
                        vertical={false}
                        strokeDasharray="1 0"
                    />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: '12px' }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) =>
                            new Intl.NumberFormat('en-US', {
                                notation: 'compact',
                                compactDisplay: 'short',
                            }).format(value)
                        }
                        width={40}
                        tick={{ fontSize: '12px' }}
                    />
                    <Tooltip content={(item) => formatTooltip(item)} />
                    {years
                        .map((year: any, index) => (
                            <Area
                                key={year}
                                type="monotone"
                                dataKey={year}
                                name={year}
                                stroke={colors[index]}
                                strokeWidth={2}
                                fill={colors[index]}
                                fillOpacity={0.1}
                                legendType="circle"
                                activeDot={{
                                    stroke: colors[index],
                                    strokeWidth: 3,
                                    r: 2.5,
                                }}
                            />
                        ))
                        .reverse()}
                    <Legend
                        content={renderLegend}
                        verticalAlign="top"
                        align="left"
                    />
                </AreaChart>
            </div>
        </ChartContainer>
    )
}

export default LegendYearOverYearOriginationsChart
